import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useAdminTheme } from '../../context/AdminThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Separator } from '../../components/ui/separator';
import { 
  ShoppingCart, Plus, Minus, Trash2, CreditCard, 
  Banknote, Receipt, Package, Search, Eye, RefreshCw, Globe, Monitor,
  Calendar, Wallet, ExternalLink, CheckCircle, Clock, XCircle, Loader2,
  Shield, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

// Supports setting REACT_APP_BACKEND_URL as either:
// - base host (e.g. http://localhost:8000)
// - full API base (e.g. https://qa.pili-grove.plgucamsur.com/api)
// We normalize by trimming trailing slashes and removing a trailing `/api`.
const normalizeBackendBase = (rawValue) => {
  const fallback = 'http://localhost:8000';
  const raw = String(rawValue || '').trim();
  if (!raw) return fallback;

  const noTrailingSlash = raw.replace(/\/+$/g, '');
  return noTrailingSlash.replace(/\/api$/i, '');
};

const BACKEND_BASE = normalizeBackendBase(process.env.REACT_APP_BACKEND_URL);
const API = `${BACKEND_BASE}/api`;

// Kiosk API endpoints
const KIOSK_API_BASE = process.env.REACT_APP_KIOSK_API_BASE || 'https://pickleball-kiosk.plgucamsur.com/api';
const KIOSK_TOKEN_URL = `${KIOSK_API_BASE}/kiosk/token`;
const KIOSK_TRANSACTIONS_URL = `${KIOSK_API_BASE}/transactions`;

// VisitCamsur Payment API (UnionBank)
// Use mock for development, production endpoint for live
const USE_MOCK_PAYMENT = process.env.REACT_APP_USE_MOCK_PAYMENT !== 'false';
const PAYMENT_API_BASE = 'https://visitcamsur.com';
const PAYMENT_CREATE_LINK_URL = USE_MOCK_PAYMENT 
  ? `${PAYMENT_API_BASE}/mock-payment/create-link`
  : `${PAYMENT_API_BASE}/unionbank`;

// Token cache to avoid fetching on every request
let kioskTokenCache = { token: null, expiresAt: null };

// WebSocket URL helper
const getWebSocketUrl = () => {
  const wsBase = BACKEND_BASE.replace(/^http/i, 'ws');
  return `${wsBase}/api/ws`;
};

const AdminPOSPage = () => {
  const { token, admin } = useAdminAuth();
  const { isDarkMode, theme } = useAdminTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [txLoading, setTxLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [customerName, setCustomerName] = useState('');
  const [transactionRef, setTransactionRef] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  // Modal state for transaction details
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [confirmingTransaction, setConfirmingTransaction] = useState(false);
  const [amountPaid, setAmountPaid] = useState('');
  const [changeAmount, setChangeAmount] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');
  const amountInputRef = useRef(null);
  
  // Source filter: 'all', 'kiosk', 'web'
  const [sourceFilter, setSourceFilter] = useState('all');
  
  // Payment status filter: 'all', 'pending', 'paid'
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  
  // Web booking payment confirmation state
  const [confirmingWebPayment, setConfirmingWebPayment] = useState(false);
  const [selectedWebPaymentMethod, setSelectedWebPaymentMethod] = useState('cash');
  
  // Online payment link state
  const [creatingPaymentLink, setCreatingPaymentLink] = useState(false);
  
  // Payment confirmation dialog state (for UnionBank)
  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false);
  
  // Payment callback modal state
  const [paymentCallbackModal, setPaymentCallbackModal] = useState(false);
  const [paymentCallbackStatus, setPaymentCallbackStatus] = useState('checking'); // 'checking', 'success', 'pending', 'failed'
  const [paymentCallbackError, setPaymentCallbackError] = useState('');
  const [paymentCallbackRef, setPaymentCallbackRef] = useState('');

  // Handle payment callback from URL parameters
  useEffect(() => {
    const handlePaymentCallback = async () => {
      const paymentStatus = searchParams.get('payment_status');
      const referenceNo = searchParams.get('ref') || searchParams.get('reference_no');
      const urlStatus = searchParams.get('status');
      const kioskTxCode = searchParams.get('kiosk_tx'); // Kiosk transaction code
      
      // Check if we're returning from a payment
      if (paymentStatus === 'success' || referenceNo) {
        setPaymentCallbackModal(true);
        setPaymentCallbackStatus('checking');
        setPaymentCallbackRef(referenceNo || '');
        
        // Clear URL parameters
        setSearchParams({});
        
        // If status=cancelled was returned
        if (urlStatus === 'cancelled') {
          setPaymentCallbackStatus('failed');
          setPaymentCallbackError('Payment was cancelled by customer');
          return;
        }
        
        // For successful payment, confirm both backend and kiosk
        if (paymentStatus === 'success' || referenceNo) {
          let kioskConfirmed = false;
          
          // STEP 1: Confirm kiosk transaction directly via Kiosk API
          if (kioskTxCode) {
            try {
              console.log('[KIOSK] Confirming transaction:', kioskTxCode);
              const kioskToken = await getKioskToken();
              const kioskUrl = `${KIOSK_TRANSACTIONS_URL}/${kioskTxCode}/confirm`;
              
              const kioskRes = await fetch(kioskUrl, {
                method: 'POST',
                headers: { 
                  'X-KIOSK-TOKEN': kioskToken,
                  'Content-Type': 'application/json',
                },
              });
              
              if (kioskRes.ok) {
                const kioskData = await kioskRes.json();
                console.log('[KIOSK] Transaction confirmed:', kioskData);
                kioskConfirmed = true;
              } else {
                console.error('[KIOSK] Failed to confirm:', kioskRes.status);
              }
            } catch (err) {
              console.error('[KIOSK] Error confirming transaction:', err);
            }
          }
          
          // STEP 2: Also try to confirm via backend (for web bookings record)
          if (referenceNo) {
            try {
              const confirmResponse = await fetch(`${API}/admin/payments/confirm`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  reference_no: referenceNo,
                  payment_method: 'unionbank'
                })
              });
              
              if (confirmResponse.ok) {
                console.log('[BACKEND] Payment confirmed');
              }
            } catch (err) {
              console.error('[BACKEND] Error confirming payment:', err);
            }
          }
          
          // Show success status
          setPaymentCallbackStatus('success');
          const kioskMsg = kioskConfirmed ? ` (Kiosk ${kioskTxCode} updated to paid)` : '';
          toast.success(`Payment confirmed successfully!${kioskMsg}`);
          
          // Refresh transactions to show updated status
          setTimeout(() => {
            fetchTransactions();
          }, 1000);
          return;
        }
        
        // Fallback: show pending if we couldn't confirm
        setPaymentCallbackStatus('pending');
      }
    };
    
    handlePaymentCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    fetchItems();
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Realtime updates via WebSocket (auto-reconnect)
  useEffect(() => {
    if (!token) return;

    let ws;
    let reconnectTimer;
    let reconnectDelayMs = 2000;

    const connect = () => {
      try {
        ws = new WebSocket(getWebSocketUrl());

        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            if (!msg || typeof msg !== 'object') return;

            // Handle various transaction/booking updates
            if (msg.type === 'booking_created' || msg.type === 'booking_updated' || 
                msg.type === 'payment_confirmed' || msg.type === 'transaction_updated' ||
                msg.type === 'kiosk_transaction_updated') {
              // Refresh transactions to get latest data
              fetchTransactions();
            }

            // Handle equipment/POS item updates
            if (msg.type === 'equipment_created' || msg.type === 'equipment_updated' || 
                msg.type === 'equipment_deleted' || msg.type === 'pos_item_updated') {
              fetchItems();
            }
          } catch {
            // ignore malformed messages
          }
        };

        ws.onopen = () => {
          reconnectDelayMs = 2000;
          console.log('✅ POS WebSocket connected for realtime updates');
        };

        ws.onclose = () => {
          if (reconnectTimer) clearTimeout(reconnectTimer);
          reconnectTimer = setTimeout(() => {
            reconnectDelayMs = Math.min(reconnectDelayMs * 2, 30000);
            connect();
          }, reconnectDelayMs);
        };

        ws.onerror = () => {
          try { ws.close(); } catch { /* ignore */ }
        };
      } catch (error) {
        console.warn('WebSocket connection failed:', error);
      }
    };

    connect();
    return () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { ws?.close(); } catch { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const normalizeStatus = (s) => {
    const st = String(s || '').toLowerCase();
    return st === 'pending' ? 'reserved' : st;
  };

  const statusClassFor = (st) => {
    if (st === 'paid') return 'text-emerald-500';
    if (st === 'reserved' || st === 'unpaid') return 'text-amber-500';
    return isDarkMode ? 'text-slate-200' : 'text-gray-800';
  };

  const formatBookingSummary = (bookingInfos) => {
    const infos = Array.isArray(bookingInfos) ? bookingInfos : [];
    if (infos.length === 0) {
      return { courts: ['-'], dates: ['-'], times: ['-'], players: ['-'] };
    }

    const courts = infos.map((info) => (info?.court_no != null ? `Court ${info.court_no}` : info?.court || '-'));
    const dates = infos.map((info) => info?.booking_date || '-');
    const times = infos.map((info) => {
      if (info?.booking_time_start && info?.booking_time_end) {
        return `${info.booking_time_start} - ${info.booking_time_end}`;
      }
      return info?.time_slot || '-';
    });
    const players = infos.map((info) => {
      if (typeof info?.player_count === 'number') return String(info.player_count);
      if (typeof info?.players === 'number') return String(info.players);
      return '-';
    });

    return { courts, dates, times, players };
  };

  const fetchItems = async () => {
    try {
      const res = await fetch(`${API}/admin/pos/items`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setItems(await res.json());
      }
    } catch (error) {
      console.error('Failed to fetch items:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get or refresh kiosk token
  const getKioskToken = async () => {
    const now = Date.now();
    
    if (kioskTokenCache.token && kioskTokenCache.expiresAt && kioskTokenCache.expiresAt > now + 60000) {
      return kioskTokenCache.token;
    }

    try {
      const res = await fetch(KIOSK_TOKEN_URL, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ pos_id: 'cwc' }),
        mode: 'cors',
      });

      if (!res.ok) {
        const errorText = await res.text().catch(() => '');
        console.error('Kiosk token request failed:', res.status, errorText);
        throw new Error(`Failed to get kiosk token: ${res.status}`);
      }

      const data = await res.json();
      const token = data?.token;
      const expiresIn = data?.expires_in || 600;

      if (!token) {
        throw new Error('No token in response');
      }

      kioskTokenCache = {
        token,
        expiresAt: now + (expiresIn * 1000) - 30000,
      };

      return token;
    } catch (err) {
      console.error('Failed to get kiosk token:', err);
      toast.error(`Failed to authenticate with kiosk API: ${err.message}`);
      throw err;
    }
  };

  // Generate a unique 12-character alphabetic transaction ID for VisitCamsur API
  const generateTransactionId = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let result = '';
    for (let i = 0; i < 12; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // Create payment link via VisitCamsur API (UnionBank)
  const createPaymentLink = async (transaction) => {
    if (!transaction) {
      toast.error('No transaction selected');
      return null;
    }

    setCreatingPaymentLink(true);

    try {
      const amount = Number(transaction.total_amount || transaction.amount || 0);
      const customerName = transaction.name || transaction.customer_name || 'Customer';
      const customerEmail = transaction.email || transaction.customer_email || 'customer@example.com';
      const customerPhone = transaction.contact_number || transaction.customer_phone || '639000000000';
      
      // Get kiosk transaction code if this is a kiosk transaction
      const kioskTransactionCode = transaction.transaction_code || null;
      
      // Format phone number (ensure it starts with 63)
      let formattedPhone = customerPhone.replace(/[^0-9]/g, '');
      if (formattedPhone.startsWith('0')) {
        formattedPhone = '63' + formattedPhone.slice(1);
      } else if (!formattedPhone.startsWith('63')) {
        formattedPhone = '63' + formattedPhone;
      }

      // Generate unique transaction ID (12 alphabetic characters)
      const paymentTransactionId = generateTransactionId();
      
      // Return URL goes back to POS Bookings page after payment
      // Include kiosk transaction code for confirmation
      let returnUrl = `${window.location.origin}/admin/pos-bookings?payment_status=success&ref=${paymentTransactionId}`;
      if (kioskTransactionCode) {
        returnUrl += `&kiosk_tx=${kioskTransactionCode}`;
      }

      // Call backend to create payment link (avoids CORS issues)
      const response = await fetch(`${API}/admin/payments/create-link`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: amount,
          email_address: customerEmail,
          mobile_no: formattedPhone,
          transaction_id: paymentTransactionId,
          name: customerName,
          return_url: returnUrl,
          booking_id: transaction.booking_id || null,
          kiosk_transaction_code: kioskTransactionCode,
          use_mock: USE_MOCK_PAYMENT
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Payment API error: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.url) {
        toast.success('Payment link created! Redirecting to payment page...');
        
        // Redirect to payment page in same window so callback works
        // The return_url will bring user back to this page with payment_status params
        window.location.href = data.url;
        
        return {
          payment_url: data.url,
          transaction_id: paymentTransactionId
        };
      } else {
        throw new Error('No payment URL returned');
      }
    } catch (e) {
      console.error('Failed to create payment link:', e);
      toast.error(`Failed to create payment link: ${e.message}`);
      return null;
    } finally {
      setCreatingPaymentLink(false);
    }
  };

  const fetchTransactions = async () => {
    setTxLoading(true);
    setError('');

    try {
      let allTransactions = [];

      // Fetch web transactions from backend (confirmed bookings + pending payments)
      try {
        const webRes = await fetch(`${API}/admin/all-transactions?source=web`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (webRes.ok) {
          const webData = await webRes.json();
          const webTx = webData.transactions || [];
          allTransactions = [...allTransactions, ...webTx];
        }
      } catch (e) {
        console.warn('Failed to fetch web transactions:', e);
      }

      // Fetch pending web bookings (bookings with pending payment status)
      try {
        const pendingRes = await fetch(`${API}/admin/bookings?payment_status=pending&limit=100`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (pendingRes.ok) {
          const pendingData = await pendingRes.json();
          const pendingBookings = pendingData.bookings || pendingData || [];
          
          // Transform pending bookings to transaction-like format
          const pendingTx = pendingBookings.map(booking => ({
            transaction_code: booking.payment_transaction_id || booking.booking_id || `WEB${booking.booking_id?.slice(-8)?.toUpperCase() || ''}`,
            booking_id: booking.booking_id,
            customer_name: booking.customer_name || booking.user_name || 'Customer',
            customer_email: booking.customer_email || booking.user_email || '',
            customer_phone: booking.customer_phone || booking.user_phone || '',
            court_id: booking.court_id,
            court_name: booking.court_name || `Court ${booking.court_id}`,
            booking_date: booking.date,
            start_time: booking.start_time,
            end_time: booking.end_time,
            time_slots: booking.time_slots || [],
            duration_hours: booking.duration_hours,
            total_amount: booking.total_amount,
            amount: booking.total_amount,
            payment_status: booking.payment_status || 'pending',
            payment_method: booking.payment_method,
            payment_url: booking.payment_url,
            status: booking.status,
            source: 'web',
            transaction_type: 'online_booking',
            equipment_rentals: booking.equipment_rentals || [],
            created_at: booking.created_at,
            is_pending_booking: true // Flag to identify pending bookings
          }));
          
          // Filter out duplicates (in case they're already in web transactions)
          const existingCodes = new Set(allTransactions.map(t => t.transaction_code));
          const newPending = pendingTx.filter(t => !existingCodes.has(t.transaction_code));
          allTransactions = [...allTransactions, ...newPending];
        }
      } catch (e) {
        console.warn('Failed to fetch pending bookings:', e);
      }

      // Fetch kiosk transactions
      try {
        const kioskToken = await getKioskToken();
        const res = await fetch(KIOSK_TRANSACTIONS_URL, {
          headers: { 'X-KIOSK-TOKEN': kioskToken },
        });

        if (res.ok) {
          const responseData = await res.json();
          const txArray = Array.isArray(responseData?.data) ? responseData.data : [];
          const kioskTx = txArray.map(tx => ({ ...tx, source: 'kiosk' }));
          allTransactions = [...allTransactions, ...kioskTx];
        }
      } catch (e) {
        console.warn('Failed to fetch kiosk transactions:', e);
      }

      // Sort by created_at descending
      allTransactions.sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      });

      setTransactions(allTransactions);
    } catch (e) {
      console.error('Failed to fetch transactions:', e);
      setTransactions([]);
      setError(e.message || 'Failed to load transactions');
    } finally {
      setTxLoading(false);
    }
  };

  const addToCart = (item) => {
    const existing = cart.find(c => c.item_id === item.item_id);
    if (existing) {
      setCart(cart.map(c => 
        c.item_id === item.item_id 
          ? { ...c, quantity: c.quantity + 1 }
          : c
      ));
    } else {
      setCart([...cart, { ...item, quantity: 1 }]);
    }
  };

  const updateQuantity = (itemId, delta) => {
    setCart(cart.map(c => {
      if (c.item_id === itemId) {
        const newQty = c.quantity + delta;
        return newQty > 0 ? { ...c, quantity: newQty } : null;
      }
      return c;
    }).filter(Boolean));
  };

  const removeFromCart = (itemId) => {
    setCart(cart.filter(c => c.item_id !== itemId));
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const processOrder = async () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    if (!customerName.trim()) {
      toast.error('Customer name is required');
      return;
    }

    if (paymentMethod !== 'cash' && !transactionRef.trim()) {
      toast.error('Transaction reference number is required for non-cash payments');
      return;
    }

    setProcessing(true);
    try {
      const res = await fetch(`${API}/admin/pos/orders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: cart.map(item => ({
            item_id: item.item_id,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            category: item.category
          })),
          payment_method: paymentMethod,
          customer_name: customerName.trim(),
          transaction_ref: paymentMethod !== 'cash' ? transactionRef.trim() : null,
          customer_id: selectedCustomer?.user_id,
          notes: ''
        })
      });

      if (res.ok) {
        const order = await res.json();
        toast.success(`Order #${order.order_id.slice(-6)} completed`);
        setCart([]);
        setCustomerName('');
        setTransactionRef('');
        setSelectedCustomer(null);
        fetchItems(); // Refresh inventory
      } else {
        toast.error('Failed to process order');
      }
    } catch (error) {
      toast.error('Error processing order');
    } finally {
      setProcessing(false);
    }
  };

  // Select transaction for the right panel (quick view)
  const selectTransactionForPanel = async (transaction) => {
    setAmountPaid('');
    setChangeAmount(null);

    try {
      // If it's a web transaction, we already have all the data
      if (transaction.source === 'web') {
        setSelectedTransaction(transaction);
        return;
      }

      // For kiosk transactions, fetch details from kiosk API
      const kioskToken = await getKioskToken();
      const url = `${KIOSK_API_BASE}/transactions/${transaction.transaction_code}`;
      
      const res = await fetch(url, {
        headers: { 'X-KIOSK-TOKEN': kioskToken },
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch transaction details: ${res.status}`);
      }

      const responseData = await res.json();
      const txData = responseData?.data || responseData;
      txData.source = 'kiosk';
      setSelectedTransaction(txData);
    } catch (e) {
      console.error('Failed to fetch transaction details:', e);
      toast.error(`Failed to load transaction: ${e.message}`);
      // Still set the basic transaction data
      setSelectedTransaction(transaction);
    }
  };

  // Open modal for detailed view (double-click or eye button)
  const viewTransactionDetails = async (transaction) => {
    setModalOpen(true);
    setModalLoading(true);
    setAmountPaid('');
    setChangeAmount(null);

    try {
      // If it's a web transaction, we already have all the data
      if (transaction.source === 'web') {
        setSelectedTransaction(transaction);
        setModalLoading(false);
        return;
      }

      // For kiosk transactions, fetch details from kiosk API
      const kioskToken = await getKioskToken();
      const url = `${KIOSK_API_BASE}/transactions/${transaction.transaction_code}`;
      
      const res = await fetch(url, {
        headers: { 'X-KIOSK-TOKEN': kioskToken },
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch transaction details: ${res.status}`);
      }

      const responseData = await res.json();
      const txData = responseData?.data || responseData;
      txData.source = 'kiosk';
      setSelectedTransaction(txData);
    } catch (e) {
      console.error('Failed to fetch transaction details:', e);
      toast.error(`Failed to load transaction: ${e.message}`);
      setModalOpen(false);
    } finally {
      setModalLoading(false);
    }
  };

  useEffect(() => {
    if (modalOpen && !modalLoading && selectedTransaction && amountInputRef.current) {
      try {
        amountInputRef.current.focus();
        amountInputRef.current.select && amountInputRef.current.select();
      } catch (err) {
        // ignore focus errors
      }
    }
  }, [modalOpen, modalLoading, selectedTransaction]);

  const calculateChange = () => {
    if (!selectedTransaction || !amountPaid) {
      toast.error('Please enter amount paid');
      return;
    }

    // Try multiple fields where total might be stored
    const totalAmount = Number(selectedTransaction.total_amount || selectedTransaction.amount || selectedTransaction.total || 0);
    const paid = Number(amountPaid);

    if (isNaN(paid) || paid <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (totalAmount <= 0) {
      toast.error('Transaction amount not found');
      return;
    }

    const change = paid - totalAmount;
    setChangeAmount(change);

    if (change < 0) {
      toast.error('Insufficient payment amount');
    }
  };

  const confirmTransaction = async () => {
    if (confirmingTransaction) return; // Prevent multiple calls
    
    if (!selectedTransaction?.transaction_code) {
      toast.error('No transaction selected');
      return;
    }

    setConfirmingTransaction(true);
    try {
      const kioskToken = await getKioskToken();
      const url = `${KIOSK_API_BASE}/transactions/${selectedTransaction.transaction_code}/confirm`;
      
      const res = await fetch(url, {
        method: 'POST',
        headers: { 
          'X-KIOSK-TOKEN': kioskToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          payment_method: paymentMethod,
          amount_paid: Number(amountPaid) || 0,
          change_amount: changeAmount || 0
        })
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to confirm transaction: ${res.status}`);
      }

      // Save to MongoDB database
      const summary = formatBookingSummary(selectedTransaction.booking_infos);
      
      const transactionRecord = {
        transaction_code: selectedTransaction.transaction_code,
        customer_name: selectedTransaction.name || '-',
        contact: selectedTransaction.contact_number || '-',
        court: summary.courts.join(', '),
        players: summary.players.join(', '),
        booking_date: summary.dates.join(', '),
        time: summary.times.join(', '),
        payment_type: paymentMethod, // Use the selected payment method from panel
        payment_status: 'paid',
        total: Number(selectedTransaction.total_amount || 0),
        amount_paid: Number(amountPaid) || 0,
        change_amount: changeAmount || 0
      };
      
      const saveResponse = await fetch(`${BACKEND_BASE}/api/admin/kiosk-transactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(transactionRecord)
      });
      
      if (!saveResponse.ok) {
        console.error('Failed to save transaction to database');
        toast.warning('Transaction confirmed but not saved to database');
      }

      // Create bookings
      const bookingInfos = selectedTransaction.booking_infos || [];
      for (const info of bookingInfos) {
        if (!info.court_id || !info.booking_date || !info.time_slots) continue;

        try {
          const timeSlots = info.time_slots || [];
          const startTime = timeSlots.length > 0 ? timeSlots[0] : '08:00';
          const lastSlot = timeSlots.length > 0 ? timeSlots[timeSlots.length - 1] : '09:00';
          const endHour = parseInt(lastSlot.split(':')[0]) + 1;
          const endTime = `${String(endHour).padStart(2, '0')}:00`;

          const bookingPayload = {
            court_id: info.court_id,
            date: info.booking_date,
            start_time: startTime,
            end_time: endTime,
            customer_name: selectedTransaction.name || 'Kiosk Customer',
            customer_email: selectedTransaction.email || '',
            customer_phone: info.contact_no || selectedTransaction.contact_number || '',
            duration_hours: timeSlots.length,
            total_amount: Number(selectedTransaction.total_amount || 0) / bookingInfos.length,
            payment_method: 'cash',
            payment_status: 'paid',
            notes: `Kiosk Transaction: ${selectedTransaction.transaction_code}`,
            add_ons: []
          };

          await fetch(`${BACKEND_BASE}/api/admin/bookings/walk-in`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(bookingPayload)
          });
        } catch (bookingError) {
          console.error('Error creating booking:', bookingError);
        }
      }
      
      toast.success('Transaction confirmed and saved successfully');
      
      setTransactions(transactions.map(t => 
        t.transaction_code === selectedTransaction.transaction_code 
          ? { ...t, payment_status: 'paid' }
          : t
      ));
      
      setSelectedTransaction({ ...selectedTransaction, payment_status: 'paid' });
      
      setTimeout(() => {
        closeModal();
      }, 1500);
    } catch (e) {
      console.error('Failed to confirm transaction:', e);
      toast.error(`Failed to confirm: ${e.message}`);
    } finally {
      setConfirmingTransaction(false);
    }
  };

  // Confirm payment for web bookings
  const confirmWebPayment = async () => {
    if (!selectedTransaction?.booking_id) {
      toast.error('No booking selected');
      return;
    }

    setConfirmingWebPayment(true);

    try {
      // Update the booking payment status
      const res = await fetch(`${API}/admin/bookings/${selectedTransaction.booking_id}/payment`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          payment_status: 'paid',
          payment_method: selectedWebPaymentMethod,
          amount_paid: Number(amountPaid || selectedTransaction.total_amount),
          change_amount: changeAmount || 0
        })
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update payment status');
      }

      // Create transaction record for web booking
      const transactionRecord = {
        transaction_code: selectedTransaction.transaction_code || `WEB${selectedTransaction.booking_id?.slice(-8)?.toUpperCase()}`,
        booking_id: selectedTransaction.booking_id,
        customer_name: selectedTransaction.customer_name || 'Customer',
        customer_email: selectedTransaction.customer_email || '',
        customer_phone: selectedTransaction.customer_phone || '',
        court_id: selectedTransaction.court_id,
        court_name: selectedTransaction.court_name || `Court ${selectedTransaction.court_id}`,
        booking_date: selectedTransaction.booking_date,
        start_time: selectedTransaction.start_time,
        end_time: selectedTransaction.end_time,
        amount: Number(selectedTransaction.total_amount || 0),
        total_amount: Number(selectedTransaction.total_amount || 0),
        payment_status: 'paid',
        payment_method: selectedWebPaymentMethod,
        source: 'web',
        transaction_type: 'online_booking',
        created_at: new Date().toISOString()
      };

      // Save transaction record
      await fetch(`${API}/admin/transactions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(transactionRecord)
      }).catch(e => console.warn('Failed to create transaction record:', e));

      toast.success('Payment confirmed successfully');

      // Update local state
      setTransactions(transactions.map(t =>
        (t.booking_id === selectedTransaction.booking_id || t.transaction_code === selectedTransaction.transaction_code)
          ? { ...t, payment_status: 'paid' }
          : t
      ));

      setSelectedTransaction({ ...selectedTransaction, payment_status: 'paid' });

      setTimeout(() => {
        closeModal();
        fetchTransactions(); // Refresh to get latest data
      }, 1500);
    } catch (e) {
      console.error('Failed to confirm web payment:', e);
      toast.error(`Failed to confirm: ${e.message}`);
    } finally {
      setConfirmingWebPayment(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedTransaction(null);
    setAmountPaid('');
    setChangeAmount(null);
    setConfirmingWebPayment(false);
    setSelectedWebPaymentMethod('cash');
  };

  const categories = [...new Set(items.map(i => i.category))];
  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !category || item.category === category;
    return matchesSearch && matchesCategory;
  });

  const filteredTransactions = transactions.filter((t) => {
    // Apply source filter
    if (sourceFilter !== 'all') {
      if (t.source !== sourceFilter) return false;
    }
    
    // Apply payment status filter
    if (paymentStatusFilter !== 'all') {
      const status = normalizeStatus(t.payment_status || t.status);
      if (paymentStatusFilter === 'pending') {
        // Show reserved, pending, unpaid transactions
        if (status !== 'reserved' && status !== 'pending' && status !== 'unpaid') return false;
      } else if (paymentStatusFilter === 'paid') {
        if (status !== 'paid') return false;
      }
    }
    
    // Apply search filter
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    const transactionCode = String(t.transaction_code || '').toLowerCase();
    const name = String(t.name || t.customer_name || '').toLowerCase();
    const contactNumber = String(t.contact_number || t.customer_phone || '').toLowerCase();
    
    return transactionCode.includes(query) || 
           name.includes(query) || 
           contactNumber.includes(query);
  });

  // Count pending transactions for badge display
  const pendingCount = transactions.filter(t => {
    const status = normalizeStatus(t.payment_status || t.status);
    return status === 'reserved' || status === 'pending' || status === 'unpaid';
  }).length;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Transactions */}
          <div className="lg:col-span-2">
            <Card className={`${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'} h-[calc(100vh-8rem)] flex flex-col`}>
              <CardHeader className="space-y-3">
                <div className="flex flex-row items-center justify-between">
                  <CardTitle className={`${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
                    <Receipt className="h-5 w-5 text-emerald-500" />
                    All Transactions
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <div className={`${isDarkMode ? 'text-slate-400' : 'text-gray-500'} text-sm`}>{filteredTransactions.length} records</div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={fetchTransactions}
                      className={`${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Source Filter Tabs */}
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant={sourceFilter === 'all' ? 'default' : 'outline'}
                    onClick={() => setSourceFilter('all')}
                    className={sourceFilter === 'all' 
                      ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
                      : `${isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`
                    }
                  >
                    All Sources
                  </Button>
                  <Button
                    size="sm"
                    variant={sourceFilter === 'kiosk' ? 'default' : 'outline'}
                    onClick={() => setSourceFilter('kiosk')}
                    className={sourceFilter === 'kiosk' 
                      ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                      : `${isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`
                    }
                  >
                    <Monitor className="h-4 w-4 mr-1" />
                    Kiosk
                  </Button>
                  <Button
                    size="sm"
                    variant={sourceFilter === 'web' ? 'default' : 'outline'}
                    onClick={() => setSourceFilter('web')}
                    className={sourceFilter === 'web' 
                      ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                      : `${isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`
                    }
                  >
                    <Globe className="h-4 w-4 mr-1" />
                    Web Bookings
                  </Button>
                  <div className={`w-px h-6 ${isDarkMode ? 'bg-slate-600' : 'bg-gray-300'}`} />
                  <Button
                    size="sm"
                    variant={paymentStatusFilter === 'pending' ? 'default' : 'outline'}
                    onClick={() => setPaymentStatusFilter(paymentStatusFilter === 'pending' ? 'all' : 'pending')}
                    className={`relative ${paymentStatusFilter === 'pending' 
                      ? 'bg-amber-600 hover:bg-amber-700 text-white' 
                      : `${isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`
                    }`}
                  >
                    <Clock className="h-4 w-4 mr-1" />
                    Pending Payment
                    {pendingCount > 0 && (
                      <span className="ml-1.5 px-1.5 py-0.5 text-xs rounded-full bg-rose-500 text-white font-medium">
                        {pendingCount}
                      </span>
                    )}
                  </Button>
                </div>
                
                <div className="relative">
                  <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} />
                  <Input
                    placeholder="Search by code, name, or contact..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`pl-10 ${isDarkMode ? 'bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400' : 'bg-white border-gray-300 text-gray-900'}`}
                  />
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-y-auto">
                {txLoading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto"></div>
                  </div>
                ) : error ? (
                  <p className="text-rose-500 text-center py-8">{error}</p>
                ) : filteredTransactions.length === 0 ? (
                  <p className={`${isDarkMode ? 'text-slate-500' : 'text-gray-500'} text-center py-8`}>
                    {searchQuery ? 'No matching transactions found' : 'No transactions found'}
                  </p>
                ) : (
                  <div className="overflow-y-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className={`text-left text-sm ${isDarkMode ? 'text-slate-400 border-slate-700' : 'text-gray-500 border-gray-200'} border-b`}>
                          <th className="pb-3 font-medium">#</th>
                          <th className="pb-3 font-medium">Source</th>
                          <th className="pb-3 font-medium">Transaction Code</th>
                          <th className="pb-3 font-medium">Customer</th>
                          <th className="pb-3 font-medium">Contact</th>
                          <th className="pb-3 font-medium">Court</th>
                          <th className="pb-3 font-medium">Players</th>
                          <th className="pb-3 font-medium">Booking Date</th>
                          <th className="pb-3 font-medium">Time</th>
                          <th className="pb-3 font-medium">Payment Type</th>
                          <th className="pb-3 font-medium">Payment Status</th>
                          <th className="pb-3 font-medium text-right">Total</th>
                          <th className="pb-3 font-medium text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody className="text-sm">
                        {filteredTransactions.map((t, idx) => {
                          const isWebTransaction = t.source === 'web';
                          const summary = isWebTransaction
                            ? {
                                courts: [t.court_name || `Court ${t.court_id}`],
                                dates: [t.booking_date || '-'],
                                times: [t.start_time && t.end_time ? `${t.start_time} - ${t.end_time}` : '-'],
                                players: ['-']
                              }
                            : formatBookingSummary(t.booking_infos);
                          const paymentStatus = normalizeStatus(t.payment_status || t.status);
                          const isSelected = selectedTransaction && 
                            (selectedTransaction.transaction_code === t.transaction_code || 
                             selectedTransaction.booking_id === t.booking_id);

                          return (
                            <tr
                              key={t.id || t.transaction_id || t.transaction_code || idx}
                              onClick={() => selectTransactionForPanel(t)}
                              className={`${isDarkMode ? 'border-slate-700/50 hover:bg-slate-700/30' : 'border-gray-200 hover:bg-gray-50'} border-b cursor-pointer transition-colors ${isSelected ? (isDarkMode ? 'bg-black/10' : 'bg-white') : ''}`}
                            >
                              <td className={`py-3 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>{idx + 1}</td>
                              <td className="py-3">
                                <Badge 
                                  variant="outline" 
                                  className={isWebTransaction 
                                    ? 'bg-blue-100 text-blue-700 border-blue-300' 
                                    : 'bg-purple-100 text-purple-700 border-purple-300'
                                  }
                                >
                                  {isWebTransaction ? (
                                    <><Globe className="h-3 w-3 mr-1" />Web</>
                                  ) : (
                                    <><Monitor className="h-3 w-3 mr-1" />Kiosk</>
                                  )}
                                </Badge>
                              </td>
                              <td className={`py-3 font-mono text-xs ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>{t.transaction_code || '-'}</td>
                              <td className={`py-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t.name || t.customer_name || '-'}</td>
                              <td className={`py-3 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>{t.contact_number || t.customer_phone || '-'}</td>
                              <td className={`py-3 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                                <div className="space-y-1">
                                  {summary.courts.map((value, i) => (
                                    <div key={`${t.id || t.transaction_code || idx}-court-${i}`}>{value}</div>
                                  ))}
                                </div>
                              </td>
                              <td className={`py-3 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                                <div className="space-y-1">
                                  {summary.players.map((value, i) => (
                                    <div key={`${t.id || t.transaction_code || idx}-players-${i}`}>{value}</div>
                                  ))}
                                </div>
                              </td>
                              <td className={`py-3 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                                <div className="space-y-1">
                                  {summary.dates.map((value, i) => (
                                    <div key={`${t.id || t.transaction_code || idx}-date-${i}`}>{value}</div>
                                  ))}
                                </div>
                              </td>
                              <td className={`py-3 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                                <div className="space-y-1">
                                  {summary.times.map((value, i) => (
                                    <div key={`${t.id || t.transaction_code || idx}-time-${i}`}>{value}</div>
                                  ))}
                                </div>
                              </td>
                              <td className={`py-3 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{t.payment_type || t.payment_method || '-'}</td>
                              <td className="py-3">
                                {paymentStatus === 'paid' ? (
                                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Paid
                                  </Badge>
                                ) : paymentStatus === 'pending' || paymentStatus === 'reserved' || paymentStatus === 'unpaid' ? (
                                  <Badge className="bg-amber-100 text-amber-700 border-amber-300">
                                    <Clock className="h-3 w-3 mr-1" />
                                    {paymentStatus === 'reserved' ? 'Reserved' : 'Pending'}
                                  </Badge>
                                ) : paymentStatus === 'expired' ? (
                                  <Badge className={isDarkMode ? 'bg-rose-900/20 text-black border-rose-700/30' : 'bg-rose-100 text-rose-600 border-rose-300'}>
                                    <XCircle className="h-3 w-3 mr-1" />
                                    Expired
                                  </Badge>
                                ) : paymentStatus === 'cancelled' || paymentStatus === 'failed' ? (
                                  <Badge className="bg-rose-100 text-rose-700 border-rose-300">
                                    <XCircle className="h-3 w-3 mr-1" />
                                    {paymentStatus}
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className={isDarkMode ? 'border-slate-600' : 'border-gray-300'}>
                                    {paymentStatus || '-'}
                                  </Badge>
                                )}
                              </td>
                              <td className="py-3 text-right text-emerald-500 font-medium">₱{Number(t.total_amount || t.amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                              <td className="py-3 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => viewTransactionDetails(t)}
                                    className={`h-8 w-8 ${isDarkMode ? 'hover:bg-slate-700 text-slate-300' : 'hover:bg-gray-100 text-gray-600'}`}
                                    title="View Details"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  {isWebTransaction && t.payment_url && paymentStatus !== 'paid' && (
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      onClick={() => window.open(t.payment_url, '_blank')}
                                      className={`h-8 w-8 ${isDarkMode ? 'hover:bg-slate-700 text-blue-400' : 'hover:bg-gray-100 text-blue-600'}`}
                                      title="Open Payment Link"
                                    >
                                      <ExternalLink className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Selected Booking Panel */}
          <div className="flex flex-col h-[calc(100vh-8rem)] overflow-x-hidden">
            <Card className={`${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'} flex-1 flex flex-col`}>
              <CardHeader className="pb-2">
                <CardTitle className={`${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
                  <Calendar className="h-5 w-5 text-emerald-500" />
                  Selected Booking
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col">
                {/* Selected Transaction Details */}
                <div className="flex-1 overflow-y-auto space-y-4 mb-4">
                  {!selectedTransaction ? (
                    <div className="text-center py-12">
                      <div className={`${isDarkMode ? 'text-slate-500' : 'text-gray-400'} mb-3`}>
                        <Eye className="h-12 w-12 mx-auto opacity-50" />
                      </div>
                      <p className={`${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                        Select a transaction from the list to view details
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Source Badge */}
                      <div className="flex items-center gap-2">
                        <Badge 
                          variant="outline" 
                          className={selectedTransaction.source === 'web' 
                            ? 'bg-blue-100 text-blue-700 border-blue-300' 
                            : 'bg-purple-100 text-purple-700 border-purple-300'
                          }
                        >
                          {selectedTransaction.source === 'web' ? (
                            <><Globe className="h-3 w-3 mr-1" />Online Booking</>
                          ) : (
                            <><Monitor className="h-3 w-3 mr-1" />Kiosk</>
                          )}
                        </Badge>
                        {normalizeStatus(selectedTransaction.payment_status) === 'paid' ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Paid
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700 border-amber-300">
                            <Clock className="h-3 w-3 mr-1" />
                            Pending
                          </Badge>
                        )}
                      </div>

                      {/* Transaction Code */}
                      <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-700/30' : 'bg-gray-50'}`}>
                        <Label className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-xs`}>Transaction Code</Label>
                        <p className={`${isDarkMode ? 'text-white' : 'text-gray-900'} font-mono text-sm font-medium`}>
                          {selectedTransaction.transaction_code || selectedTransaction.booking_id || '-'}
                        </p>
                      </div>

                      {/* Customer Info */}
                      <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-700/30' : 'bg-gray-50'}`}>
                        <Label className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-xs block mb-2`}>Customer</Label>
                        <p className={`${isDarkMode ? 'text-white' : 'text-gray-900'} font-medium`}>
                          {selectedTransaction.name || selectedTransaction.customer_name || 'N/A'}
                        </p>
                        <p className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-sm`}>
                          {selectedTransaction.contact_number || selectedTransaction.customer_phone || 'No contact'}
                        </p>
                        {(selectedTransaction.email || selectedTransaction.customer_email) && (
                          <p className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-sm`}>
                            {selectedTransaction.email || selectedTransaction.customer_email}
                          </p>
                        )}
                      </div>

                      {/* Court & Booking Details */}
                      <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-emerald-900/20 border border-emerald-700/30' : 'bg-emerald-50 border border-emerald-200'}`}>
                        <Label className={`${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'} text-xs block mb-2`}>
                          <Calendar className="h-3 w-3 inline mr-1" />
                          Booking Details
                        </Label>
                        
                        {/* For Web Transactions */}
                        {selectedTransaction.source === 'web' && (
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-sm`}>Court:</span>
                              <span className={`${isDarkMode ? 'text-white' : 'text-gray-900'} font-medium`}>
                                {selectedTransaction.court_name || `Court ${selectedTransaction.court_id}` || '-'}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-sm`}>Date:</span>
                              <span className={`${isDarkMode ? 'text-white' : 'text-gray-900'} font-medium`}>
                                {selectedTransaction.booking_date || '-'}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-sm`}>Time:</span>
                              <span className={`${isDarkMode ? 'text-white' : 'text-gray-900'} font-medium`}>
                                {selectedTransaction.start_time && selectedTransaction.end_time 
                                  ? `${selectedTransaction.start_time} - ${selectedTransaction.end_time}` 
                                  : '-'}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-sm`}>Duration:</span>
                              <span className={`${isDarkMode ? 'text-white' : 'text-gray-900'} font-medium`}>
                                {selectedTransaction.duration_hours || 1} hour(s)
                              </span>
                            </div>
                          </div>
                        )}

                        {/* For Kiosk Transactions */}
                        {selectedTransaction.source !== 'web' && selectedTransaction.booking_infos && (
                          <div className="space-y-3">
                            {selectedTransaction.booking_infos.map((info, idx) => {
                              const summary = formatBookingSummary([info]);
                              return (
                                <div key={idx} className={`${idx > 0 ? `pt-2 border-t ${isDarkMode ? 'border-slate-600' : 'border-gray-200'}` : ''}`}>
                                  <div className="flex justify-between">
                                    <span className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-sm`}>Court:</span>
                                    <span className={`${isDarkMode ? 'text-white' : 'text-gray-900'} font-medium`}>{summary.courts[0]}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-sm`}>Date:</span>
                                    <span className={`${isDarkMode ? 'text-white' : 'text-gray-900'} font-medium`}>{summary.dates[0]}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-sm`}>Time:</span>
                                    <span className={`${isDarkMode ? 'text-white' : 'text-gray-900'} font-medium`}>{summary.times[0]}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-sm`}>Players:</span>
                                    <span className={`${isDarkMode ? 'text-white' : 'text-gray-900'} font-medium`}>{summary.players[0]}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* For Kiosk without booking_infos */}
                        {selectedTransaction.source !== 'web' && !selectedTransaction.booking_infos && (
                          <p className={`${isDarkMode ? 'text-slate-400' : 'text-gray-500'} text-sm`}>No booking details available</p>
                        )}
                      </div>

                      {/* Equipment Rentals */}
                      {selectedTransaction.equipment_rentals && selectedTransaction.equipment_rentals.length > 0 && (
                        <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-700/30' : 'bg-gray-50'}`}>
                          <Label className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-xs block mb-2`}>Equipment Rentals</Label>
                          {selectedTransaction.equipment_rentals.map((item, i) => (
                            <div key={i} className="flex justify-between text-sm">
                              <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>{item.name} x{item.quantity}</span>
                              <span className="text-emerald-500">₱{Number(item.price * item.quantity).toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Payment Section - Only show when a transaction is selected and not paid */}
                {selectedTransaction && normalizeStatus(selectedTransaction.payment_status) !== 'paid' && (
                  <div className={`border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-200'} pt-4 space-y-4`}>
                    {/* Payment Method */}
                    <div>
                      <Label className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-xs`}>Payment Method</Label>
                      <div className="grid grid-cols-3 gap-2 mt-1">
                        <Button
                          size="sm"
                          variant={paymentMethod === 'cash' ? 'default' : 'outline'}
                          onClick={() => { setPaymentMethod('cash'); setTransactionRef(''); }}
                          className={paymentMethod === 'cash' ? 'bg-emerald-600 text-white' : `${isDarkMode ? 'border-slate-600 text-white' : 'border-gray-300 text-gray-700'}`}
                        >
                          <Banknote className="h-4 w-4 mr-1" /> Cash
                        </Button>
                        <Button
                          size="sm"
                          variant={paymentMethod === 'card' ? 'default' : 'outline'}
                          onClick={() => setPaymentMethod('card')}
                          className={paymentMethod === 'card' ? 'bg-emerald-600 text-white' : `${isDarkMode ? 'border-slate-600 text-white' : 'border-gray-300 text-gray-700'}`}
                        >
                          <CreditCard className="h-4 w-4 mr-1" /> Card
                        </Button>
                        <Button
                          size="sm"
                          variant={paymentMethod === 'unionbank' ? 'default' : 'outline'}
                          onClick={() => setPaymentMethod('unionbank')}
                          className={paymentMethod === 'unionbank' ? 'bg-blue-600 text-white' : `${isDarkMode ? 'border-slate-600 text-white' : 'border-gray-300 text-gray-700'}`}
                        >
                          <CreditCard className="h-4 w-4 mr-1" /> UnionBank
                        </Button>
                      </div>
                    </div>

                    {/* UnionBank Online Payment - Generate Payment Link */}
                    {paymentMethod === 'unionbank' && (
                      <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-blue-900/20 border border-blue-700/30' : 'bg-blue-50 border border-blue-200'}`}>
                        <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'} mb-2`}>
                          Generate a payment link for the customer to pay via UnionBank online payment gateway.
                        </p>
                        <Button
                          size="sm"
                          onClick={() => setShowPaymentConfirmation(true)}
                          disabled={creatingPaymentLink}
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          {creatingPaymentLink ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Shield className="h-4 w-4 mr-2" />
                          )}
                          {creatingPaymentLink ? 'Creating Link...' : 'Proceed to Payment'}
                        </Button>
                        {selectedTransaction?.payment_url && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(selectedTransaction.payment_url, '_blank')}
                            className={`w-full mt-2 ${isDarkMode ? 'border-blue-500 text-blue-400' : 'border-blue-500 text-blue-600'}`}
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Open Existing Payment Link
                          </Button>
                        )}
                      </div>
                    )}

                    {/* Amount Paid & Change - Only show for non-unionbank methods */}
                    {paymentMethod !== 'unionbank' && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-xs`}>Amount Paid</Label>
                          <Input
                            ref={amountInputRef}
                            type="number"
                            step="0.01"
                            value={amountPaid}
                            onChange={(e) => {
                              setAmountPaid(e.target.value);
                              // Auto-calculate change when typing
                              if (selectedTransaction && e.target.value) {
                                const totalAmount = Number(selectedTransaction.total_amount || selectedTransaction.amount || selectedTransaction.total || 0);
                                const paid = Number(e.target.value);
                                if (!isNaN(paid) && paid > 0 && totalAmount > 0) {
                                  setChangeAmount(paid - totalAmount);
                                } else {
                                  setChangeAmount(null);
                                }
                              } else {
                                setChangeAmount(null);
                              }
                            }}
                            placeholder="0.00"
                            className={`mt-1 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                          />
                        </div>
                        <div>
                          <Label className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-xs`}>Change</Label>
                          <div className={`mt-1 h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-300'} flex items-center`}>
                            {changeAmount !== null ? (
                              <span className={`font-bold ${changeAmount < 0 ? 'text-rose-500' : 'text-emerald-500'}`}>
                                {changeAmount < 0 ? '-' : ''}₱{Math.abs(changeAmount).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            ) : (
                              <span className={`${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>-</span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Total */}
                    <div className={`flex justify-between items-center py-3 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                      <span className={`${isDarkMode ? 'text-slate-300' : 'text-gray-700'} font-medium`}>Total Amount</span>
                      <span className="text-2xl font-bold text-emerald-500">
                        ₱{Number(selectedTransaction.total_amount || selectedTransaction.amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>

                    {/* Action Buttons - Only show for non-unionbank methods */}
                    {paymentMethod !== 'unionbank' && (
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={calculateChange}
                          className={`flex-1 ${isDarkMode ? 'border-slate-600 text-white hover:bg-slate-700' : 'border-gray-300 text-gray-900 hover:bg-gray-100'}`}
                        >
                          Calculate
                        </Button>
                        <Button
                          className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700 text-white text-lg"
                          onClick={selectedTransaction.source === 'web' ? confirmWebPayment : confirmTransaction}
                          disabled={(changeAmount === null || changeAmount < 0) || confirmingWebPayment || confirmingTransaction}
                        >
                          {confirmingWebPayment || confirmingTransaction ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          ) : (
                            <>
                              <CheckCircle className="h-5 w-5 mr-2" /> Confirm Payment
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Already Paid Message */}
                {selectedTransaction && normalizeStatus(selectedTransaction.payment_status) === 'paid' && (
                  <div className={`border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-200'} pt-4`}>
                    <div className={`flex justify-between items-center py-3`}>
                      <span className={`${isDarkMode ? 'text-slate-300' : 'text-gray-700'} font-medium`}>Total Paid</span>
                      <span className="text-2xl font-bold text-emerald-500">
                        ₱{Number(selectedTransaction.total_amount || selectedTransaction.amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                      <p className="text-emerald-500 text-center font-medium">
                        <CheckCircle className="h-5 w-5 inline mr-2" />
                        Payment Confirmed
                      </p>
                    </div>
                  </div>
                )}

                {/* Clear Selection Button */}
                {selectedTransaction && (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setSelectedTransaction(null);
                      setAmountPaid('');
                      setChangeAmount(null);
                    }}
                    className={`w-full mt-2 ${isDarkMode ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}
                  >
                    Clear Selection
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Transaction Details Modal */}
        <Dialog open={modalOpen} onOpenChange={closeModal}>
          <DialogContent className={`max-w-2xl ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            <DialogHeader>
              <DialogTitle className={`${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Transaction Details
              </DialogTitle>
            </DialogHeader>
            
            {modalLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto"></div>
              </div>
            ) : selectedTransaction ? (
              <div className="space-y-4">
                {/* Source Badge */}
                <div className="flex items-center gap-2">
                  <Badge 
                    variant="outline" 
                    className={selectedTransaction.source === 'web' 
                      ? 'bg-blue-100 text-blue-700 border-blue-300' 
                      : 'bg-purple-100 text-purple-700 border-purple-300'
                    }
                  >
                    {selectedTransaction.source === 'web' ? (
                      <><Globe className="h-3 w-3 mr-1" />Online Booking</>
                    ) : (
                      <><Monitor className="h-3 w-3 mr-1" />Kiosk Transaction</>
                    )}
                  </Badge>
                  {selectedTransaction.is_pending_booking && (
                    <Badge className="bg-amber-100 text-amber-700 border-amber-300">
                      <Clock className="h-3 w-3 mr-1" />
                      Awaiting Payment
                    </Badge>
                  )}
                </div>

                {/* Transaction Info */}
                <div className={`grid grid-cols-2 gap-4 p-4 rounded-lg ${isDarkMode ? 'bg-slate-700/30' : 'bg-gray-50'}`}>
                  <div>
                    <Label className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-xs`}>Transaction Code</Label>
                    <p className={`${isDarkMode ? 'text-white' : 'text-gray-900'} font-mono text-sm`}>{selectedTransaction.transaction_code || selectedTransaction.booking_id || '-'}</p>
                  </div>
                  <div>
                    <Label className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-xs`}>Customer Name</Label>
                    <p className={`${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{selectedTransaction.name || selectedTransaction.customer_name || '-'}</p>
                  </div>
                  <div>
                    <Label className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-xs`}>Contact</Label>
                    <p className={`${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{selectedTransaction.contact_number || selectedTransaction.customer_phone || '-'}</p>
                  </div>
                  <div>
                    <Label className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-xs`}>Email</Label>
                    <p className={`${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{selectedTransaction.email || selectedTransaction.customer_email || '-'}</p>
                  </div>
                  <div>
                    <Label className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-xs`}>Payment Method</Label>
                    <p className={`${isDarkMode ? 'text-white' : 'text-gray-900'} capitalize`}>{selectedTransaction.payment_type || selectedTransaction.payment_method || '-'}</p>
                  </div>
                  <div>
                    <Label className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-xs`}>Payment Status</Label>
                    <div className="mt-1">
                      {(() => {
                        const st = normalizeStatus(selectedTransaction.payment_status);
                        if (st === 'paid') {
                          return (
                            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Paid
                            </Badge>
                          );
                        }

                        if (st === 'expired') {
                          return (
                            <Badge className={isDarkMode ? 'bg-rose-900/20 text-black border-rose-700/30' : 'bg-rose-100 text-rose-600 border-rose-300'}>
                              <XCircle className="h-3 w-3 mr-1" />
                              Expired
                            </Badge>
                          );
                        }

                        return (
                          <Badge className="bg-amber-100 text-amber-700 border-amber-300">
                            <Clock className="h-3 w-3 mr-1" />
                            {st || 'Pending'}
                          </Badge>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <Label className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-xs`}>Total Amount</Label>
                    <p className="text-emerald-500 font-bold text-xl">₱{Number(selectedTransaction.total_amount || selectedTransaction.amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  </div>
                </div>

                {/* Web Booking Details */}
                {selectedTransaction.source === 'web' && (
                  <div>
                    <Label className={`${isDarkMode ? 'text-slate-300' : 'text-gray-700'} font-medium mb-2 block`}>Booking Details</Label>
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-700/30' : 'bg-gray-50'}`}>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-xs`}>Court: </span>
                          <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>{selectedTransaction.court_name || `Court ${selectedTransaction.court_id}`}</span>
                        </div>
                        <div>
                          <span className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-xs`}>Date: </span>
                          <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>{selectedTransaction.booking_date || '-'}</span>
                        </div>
                        <div>
                          <span className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-xs`}>Time: </span>
                          <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                            {selectedTransaction.start_time && selectedTransaction.end_time 
                              ? `${selectedTransaction.start_time} - ${selectedTransaction.end_time}` 
                              : '-'}
                          </span>
                        </div>
                        <div>
                          <span className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-xs`}>Duration: </span>
                          <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>{selectedTransaction.duration_hours || 1} hour(s)</span>
                        </div>
                      </div>
                      
                      {/* Equipment Rentals */}
                      {selectedTransaction.equipment_rentals && selectedTransaction.equipment_rentals.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-600/50">
                          <span className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-xs block mb-2`}>Equipment Rentals:</span>
                          {selectedTransaction.equipment_rentals.map((item, i) => (
                            <div key={i} className="flex justify-between text-sm">
                              <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>{item.name} x{item.quantity}</span>
                              <span className="text-emerald-500">₱{Number(item.price * item.quantity).toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Payment URL for pending web bookings */}
                      {selectedTransaction.payment_url && normalizeStatus(selectedTransaction.payment_status) !== 'paid' && (
                        <div className="mt-3 pt-3 border-t border-slate-600/50">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(selectedTransaction.payment_url, '_blank')}
                            className={`w-full ${isDarkMode ? 'border-blue-500 text-blue-400 hover:bg-blue-500/10' : 'border-blue-500 text-blue-600 hover:bg-blue-50'}`}
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Open Payment Link
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Kiosk Booking Details */}
                {selectedTransaction.source !== 'web' && selectedTransaction.booking_infos && selectedTransaction.booking_infos.length > 0 && (
                  <div>
                    <Label className={`${isDarkMode ? 'text-slate-300' : 'text-gray-700'} font-medium mb-2 block`}>Booking Details</Label>
                    <div className={`space-y-2 p-4 rounded-lg ${isDarkMode ? 'bg-slate-700/30' : 'bg-gray-50'}`}>
                      {selectedTransaction.booking_infos.map((info, idx) => {
                        const summary = formatBookingSummary([info]);
                        return (
                          <div key={idx} className={`pb-2 ${idx < selectedTransaction.booking_infos.length - 1 ? `border-b ${isDarkMode ? 'border-slate-600' : 'border-gray-200'}` : ''}`}>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <span className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-xs`}>Court: </span>
                                <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>{summary.courts[0]}</span>
                              </div>
                              <div>
                                <span className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-xs`}>Players: </span>
                                <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>{summary.players[0]}</span>
                              </div>
                              <div>
                                <span className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-xs`}>Date: </span>
                                <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>{summary.dates[0]}</span>
                              </div>
                              <div>
                                <span className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-xs`}>Time: </span>
                                <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>{summary.times[0]}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Payment Input - only show if not paid */}
                {normalizeStatus(selectedTransaction.payment_status) !== 'paid' && (
                  <div className="space-y-4">
                    {/* Payment Method Selection for Web Bookings */}
                    {selectedTransaction.source === 'web' && (
                      <div>
                        <Label className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-xs mb-2 block`}>Payment Method (for manual confirmation)</Label>
                        <div className="grid grid-cols-3 gap-2">
                          {['cash', 'card', 'unionbank'].map(method => (
                            <Button
                              key={method}
                              size="sm"
                              variant={selectedWebPaymentMethod === method ? 'default' : 'outline'}
                              onClick={() => setSelectedWebPaymentMethod(method)}
                              className={selectedWebPaymentMethod === method 
                                ? (method === 'unionbank' ? 'bg-blue-600 text-white' : 'bg-emerald-600 text-white')
                                : `${isDarkMode ? 'border-slate-600 text-white' : 'border-gray-300 text-gray-700'}`
                              }
                            >
                              {method === 'cash' && <Banknote className="h-3 w-3 mr-1" />}
                              {(method === 'card' || method === 'unionbank') && <CreditCard className="h-3 w-3 mr-1" />}
                              {method === 'unionbank' ? 'UnionBank' : method.charAt(0).toUpperCase() + method.slice(1).replace('_', ' ')}
                            </Button>
                          ))}
                        </div>
                        
                        {/* UnionBank Payment Link Option in Modal */}
                        {selectedWebPaymentMethod === 'unionbank' && (
                          <div className={`mt-3 p-3 rounded-lg ${isDarkMode ? 'bg-blue-900/20 border border-blue-700/30' : 'bg-blue-50 border border-blue-200'}`}>
                            <p className={`text-xs ${isDarkMode ? 'text-blue-300' : 'text-blue-700'} mb-2`}>
                              Generate a payment link for UnionBank online payment.
                            </p>
                            <Button
                              size="sm"
                              onClick={() => setShowPaymentConfirmation(true)}
                              disabled={creatingPaymentLink}
                              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                            >
                              {creatingPaymentLink ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <Shield className="h-4 w-4 mr-2" />
                              )}
                              {creatingPaymentLink ? 'Creating...' : 'Proceed to Payment'}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Amount Paid & Change - Only for non-unionbank */}
                    {selectedWebPaymentMethod !== 'unionbank' && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-xs`}>Enter Amount Paid</Label>
                          <Input
                            ref={amountInputRef}
                            type="number"
                            step="0.01"
                            value={amountPaid}
                            onChange={(e) => setAmountPaid(e.target.value)}
                            placeholder="0.00"
                            className={`mt-1 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                          />
                        </div>
                        <div>
                          <Label className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} text-xs`}>Change</Label>
                          <div className={`mt-1 h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-300'} flex items-center`}>
                            {changeAmount !== null ? (
                              <span className={`font-bold ${changeAmount < 0 ? 'text-rose-500' : 'text-emerald-500'}`}>
                                ₱{Math.abs(changeAmount).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            ) : (
                              <span className={`${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>-</span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Confirm buttons - Only for non-unionbank */}
                    {selectedWebPaymentMethod !== 'unionbank' && (
                      <div className="flex gap-2">
                        <Button
                          onClick={calculateChange}
                          variant="outline"
                          className={`flex-1 ${isDarkMode ? 'border-slate-600 text-white hover:bg-slate-700' : 'border-gray-300 text-gray-900 hover:bg-gray-100'}`}
                        >
                          Calculate Change
                        </Button>
                        <Button
                          onClick={selectedTransaction.source === 'web' ? confirmWebPayment : confirmTransaction}
                          disabled={(changeAmount === null || changeAmount < 0) || confirmingWebPayment || confirmingTransaction}
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          {confirmingWebPayment || confirmingTransaction ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Confirm Payment
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Already Paid Message */}
                {normalizeStatus(selectedTransaction.payment_status) === 'paid' && (
                  <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                    <p className="text-emerald-500 text-center font-medium">
                      ✓ This transaction has been paid
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <p className={`${isDarkMode ? 'text-slate-500' : 'text-gray-500'} text-center py-8`}>No transaction data</p>
            )}
          </DialogContent>
        </Dialog>

        {/* Payment Callback Modal */}
        <Dialog open={paymentCallbackModal} onOpenChange={setPaymentCallbackModal}>
          <DialogContent className={`max-w-md ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            <DialogHeader>
              <DialogTitle className={`${isDarkMode ? 'text-white' : 'text-gray-900'} text-center`}>
                Payment Status
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-6">
              {paymentCallbackStatus === 'checking' && (
                <div className="text-center space-y-4">
                  <Loader2 className="h-16 w-16 mx-auto text-emerald-500 animate-spin" />
                  <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Verifying Payment...
                  </h3>
                  <p className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                    Please wait while we confirm the payment.
                  </p>
                  {paymentCallbackRef && (
                    <p className={`text-xs font-mono ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                      Reference: {paymentCallbackRef}
                    </p>
                  )}
                </div>
              )}

              {paymentCallbackStatus === 'success' && (
                <div className="text-center space-y-4">
                  <div className="h-20 w-20 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle className="h-12 w-12 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-emerald-600">
                    Payment Successful!
                  </h3>
                  <p className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                    The payment has been confirmed and recorded.
                  </p>
                  {paymentCallbackRef && (
                    <p className={`text-xs font-mono ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                      Reference: {paymentCallbackRef}
                    </p>
                  )}
                  <Button
                    onClick={() => {
                      setPaymentCallbackModal(false);
                      fetchTransactions();
                    }}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white mt-4"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Done
                  </Button>
                </div>
              )}

              {paymentCallbackStatus === 'pending' && (
                <div className="text-center space-y-4">
                  <div className="h-20 w-20 mx-auto rounded-full bg-amber-100 flex items-center justify-center">
                    <Clock className="h-12 w-12 text-amber-600" />
                  </div>
                  <h3 className="text-xl font-bold text-amber-600">
                    Payment Processing
                  </h3>
                  <p className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                    The payment is being processed. This may take a few moments.
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                    The transaction list will update automatically once payment is confirmed.
                  </p>
                  {paymentCallbackRef && (
                    <p className={`text-xs font-mono ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                      Reference: {paymentCallbackRef}
                    </p>
                  )}
                  <div className="flex gap-2 mt-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setPaymentCallbackStatus('checking');
                        // Re-check payment status
                        if (paymentCallbackRef) {
                          fetch(`${API}/admin/payments/status/${paymentCallbackRef}`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                          })
                            .then(res => res.json())
                            .then(data => {
                              if (data.payment_status === 'paid' || data.status === 'confirmed') {
                                setPaymentCallbackStatus('success');
                                fetchTransactions();
                              } else {
                                setPaymentCallbackStatus('pending');
                              }
                            })
                            .catch(() => setPaymentCallbackStatus('pending'));
                        }
                      }}
                      className={`flex-1 ${isDarkMode ? 'border-slate-600 text-white' : 'border-gray-300 text-gray-900'}`}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh Status
                    </Button>
                    <Button
                      onClick={() => {
                        setPaymentCallbackModal(false);
                        fetchTransactions();
                      }}
                      className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              )}

              {paymentCallbackStatus === 'failed' && (
                <div className="text-center space-y-4">
                  <div className="h-20 w-20 mx-auto rounded-full bg-rose-100 flex items-center justify-center">
                    <XCircle className="h-12 w-12 text-rose-600" />
                  </div>
                  <h3 className="text-xl font-bold text-rose-600">
                    Payment Failed
                  </h3>
                  <p className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                    {paymentCallbackError || 'The payment could not be processed.'}
                  </p>
                  {paymentCallbackRef && (
                    <p className={`text-xs font-mono ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                      Reference: {paymentCallbackRef}
                    </p>
                  )}
                  <Button
                    onClick={() => {
                      setPaymentCallbackModal(false);
                      setPaymentCallbackStatus('checking');
                      setPaymentCallbackError('');
                    }}
                    className="w-full bg-rose-600 hover:bg-rose-700 text-white mt-4"
                  >
                    Close
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* UnionBank Payment Confirmation Dialog */}
        <AlertDialog open={showPaymentConfirmation} onOpenChange={setShowPaymentConfirmation}>
          <AlertDialogContent className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}>
            <AlertDialogHeader>
              <AlertDialogTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                <Shield className="h-5 w-5 text-blue-500" />
                Confirm Payment Link Generation
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-4">
                  <p className={isDarkMode ? 'text-slate-400' : ''}>
                    You are about to generate a UnionBank payment link for the customer. Please review the details below:
                  </p>
                  
                  <div className={`rounded-lg p-4 space-y-2 ${isDarkMode ? 'bg-slate-700/50' : 'bg-muted'}`}>
                    {selectedTransaction && (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className={isDarkMode ? 'text-slate-400' : 'text-muted-foreground'}>Transaction:</span>
                          <span className={`font-medium ${isDarkMode ? 'text-white' : ''}`}>
                            {selectedTransaction.transaction_code || selectedTransaction.booking_id || selectedTransaction.id}
                          </span>
                        </div>
                        {selectedTransaction.court_name && (
                          <div className="flex justify-between text-sm">
                            <span className={isDarkMode ? 'text-slate-400' : 'text-muted-foreground'}>Court:</span>
                            <span className={`font-medium ${isDarkMode ? 'text-white' : ''}`}>{selectedTransaction.court_name}</span>
                          </div>
                        )}
                        {(selectedTransaction.booking_date || selectedTransaction.date) && (
                          <div className="flex justify-between text-sm">
                            <span className={isDarkMode ? 'text-slate-400' : 'text-muted-foreground'}>Date:</span>
                            <span className={`font-medium ${isDarkMode ? 'text-white' : ''}`}>
                              {selectedTransaction.booking_date || selectedTransaction.date}
                            </span>
                          </div>
                        )}
                        {(selectedTransaction.start_time || selectedTransaction.time) && (
                          <div className="flex justify-between text-sm">
                            <span className={isDarkMode ? 'text-slate-400' : 'text-muted-foreground'}>Time:</span>
                            <span className={`font-medium ${isDarkMode ? 'text-white' : ''}`}>
                              {selectedTransaction.start_time ? `${selectedTransaction.start_time} - ${selectedTransaction.end_time}` : selectedTransaction.time}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm">
                          <span className={isDarkMode ? 'text-slate-400' : 'text-muted-foreground'}>Payment Method:</span>
                          <span className={`font-medium ${isDarkMode ? 'text-white' : ''}`}>UnionBank (Online)</span>
                        </div>
                        <Separator className={isDarkMode ? 'bg-slate-600' : ''} />
                        <div className="flex justify-between font-bold">
                          <span className={isDarkMode ? 'text-white' : ''}>Total Amount:</span>
                          <span className="text-blue-500">
                            ₱{(selectedTransaction.total_amount || selectedTransaction.amount || selectedTransaction.total || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  <div className={`rounded-lg p-3 ${isDarkMode ? 'bg-amber-900/30 border border-amber-700/50' : 'bg-amber-50 border border-amber-200'}`}>
                    <div className="flex items-start gap-2">
                      <AlertTriangle className={`h-4 w-4 flex-shrink-0 mt-0.5 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                      <p className={`text-sm ${isDarkMode ? 'text-amber-300' : 'text-amber-800'}`}>
                        <strong>Important:</strong> By clicking "Generate & Open Payment Link", a payment link will be created and opened in a new tab. The customer can use this link to pay{' '}
                        <strong>₱{(selectedTransaction?.total_amount || selectedTransaction?.amount || selectedTransaction?.total || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</strong>{' '}
                        via their bank card. The link will expire after 30 minutes.
                      </p>
                    </div>
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel 
                disabled={creatingPaymentLink}
                className={isDarkMode ? 'border-slate-600 text-white hover:bg-slate-700' : ''}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={(e) => {
                  e.preventDefault();
                  createPaymentLink(selectedTransaction);
                  setShowPaymentConfirmation(false);
                }}
                disabled={creatingPaymentLink}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {creatingPaymentLink ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4 mr-2" />
                    Generate & Open Payment Link
                  </>
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminPOSPage;
